import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  btag,
  NotificationManager,
  pushToDataLayer,
  sessionId,
  SessionStorageManager,
  SiteConfigManager,
} from '@atlas/core';
import { BasePopup, Button, Icon, Label } from '@atlas/ui';

import { usePostHog } from 'hooks/usePostHog';
import { DATA_LAYER_STORAGE_KEYS, POSTHOG_CTA, POSTHOG_EVENTS } from 'src/common/constants';
import StaticBlock from 'src/common/StaticBlock/StaticBlock';

const LeaveSignupPopup = ({ data: { location }, id }) => {
  const currentPath = location?.pathname;

  const postHog = usePostHog();

  useEffect(() => {
    postHog.concat(POSTHOG_EVENTS.REGISTRATION, {
      leaving_pop_up: Date.now(),
    });
  }, [postHog]);

  const popupStaticBlock = SiteConfigManager.getConfig(
    'config.config.general.leaveRegistrationPopup.popupStaticBlock',
    '',
  );

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const goToUrl = useCallback(() => {
    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CLOSED)) {
      pushToDataLayer(
        'exit_popup_closed',
        {
          method: 'close_button',
          session_id: sessionId,
        },
        btag,
      );
      SessionStorageManager.setItem(
        DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CLOSED,
        true,
      );
    }

    handleClose();
    postHog.send(POSTHOG_EVENTS.REGISTRATION, { manual_closed_timestamp: Date.now() });
    window.location.href = currentPath || '/';
  }, [handleClose, currentPath, postHog]);

  const handleCallToActionClick = useCallback(() => {
    postHog.concat(POSTHOG_EVENTS.REGISTRATION, {
      leaving_cta_clicked: POSTHOG_CTA.BACK_TO_REG,
    });

    if (
      !SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CTA_CLICKED)
    ) {
      pushToDataLayer(
        'exit_popup_cta_click',
        {
          cta: 'primary',
          page_referer: currentPath,
          session_id: sessionId,
        },
        btag,
      );
      SessionStorageManager.setItem(
        DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CTA_CLICKED,
        true,
      );
    }

    handleClose();
  }, [handleClose, currentPath, postHog]);

  const defaultContent = useMemo(
    () => (
      <div className="leave-signup-content">
        <Label className="leave-signup-title" message="leave_reg_popup_title" />
        <Label className="leave-signup-description" message="leave_reg_popup_description" />
      </div>
    ),
    [],
  );

  const content = useMemo(() => {
    if (popupStaticBlock.length > 0) {
      return (
        <StaticBlock
          className="leave-signup-static-block"
          fallback={defaultContent}
          options={{ source: popupStaticBlock }}
        />
      );
    }

    return defaultContent;
  }, [defaultContent, popupStaticBlock]);

  return (
    <BasePopup className="leave-signup-popup">
      <button className="close-popup" onClick={goToUrl} type="button">
        <Icon name="close" />
      </button>
      <div className="popup__content">{content}</div>
      <div className="popup__footer">
        <Button
          className="btn leave-signup-button"
          label="leave_reg_popup_button"
          onClick={handleCallToActionClick}
          size="big"
        />
      </div>
    </BasePopup>
  );
};

LeaveSignupPopup.propTypes = {
  data: PropTypes.shape({
    location: PropTypes.object.isRequired,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default LeaveSignupPopup;
